import { getGeocomplyClient } from '@/data/location/geocomply-common';
import { BetSelectionType } from '@/feature/bets-sbk/hooks/types';
import { SbkBetSlipState } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { Bet, GenerateBetsPayloadError, GeoComplyError, MAX_SELECTIONS, Selection } from '@/feature/betslip-sbk/types';

import { generateSgpOddsId, getComboBetType, isBetValid, isComboSelectionEnabled } from '../betslip-utils';
import { hasImportedSelection } from '../share-referrer';
import { BetPayloadError, BetSettings, BetType, PlaceBet, PlaceBetSelection, PlaceBetsRequest } from './types';

const SINGLE_ERROR_PREFIX = 'single_';
const COMBO_ERROR_PREFIX = 'combo_';
const SGP_ERROR_PREFIX = 'sgp_';
const SGP_PLUS_ERROR_PREFIX = 'sgp_plus_';

/**
 * Create geotoken and validate if the user can bet
 */
export const getGeoToken = async (): Promise<string> => {
    const geocomplyClient = getGeocomplyClient();
    const { encoded, decoded } = await geocomplyClient.request({ readCache: true });
    if (!decoded.can_bet) {
        throw new GeoComplyError(decoded);
    }
    return encoded;
};

const getCurrency = (isUsingCoins: boolean, isBetrBucksEnabled: boolean, currency: string): string => {
    return !isUsingCoins && isBetrBucksEnabled ? 'FRE' : currency;
};

const isFreeMoneyEnabled = (isUsingCoins: boolean, isBetrBucksEnabled: boolean): boolean => {
    return !isUsingCoins && isBetrBucksEnabled;
};

const getBetPromos = (state: SbkBetSlipState, betId: string, isUsingCoins: boolean): string[] => {
    // Promotions can only be applied to cash currency, not coins
    // promos are non-applicable when the bet is using betr bucks (bonus)
    if (isUsingCoins || state.isBetrBucksEnabled[betId]) {
        return [];
    }
    const campaignToken = state.selectedPromos[betId]?.campaign_token;
    return campaignToken ? [campaignToken] : [];
};

/**
 * Generates the payload for the bet slip
 */
export const generateBetsPayload = (
    userId: string,
    state: SbkBetSlipState,
    geotoken: string,
    betSettings: BetSettings,
    isUsingCoins: boolean
): PlaceBetsRequest => {
    const { referrer } = state;
    return {
        customer: {
            user_id: userId,
            geotoken,
        },
        bets: [
            ...Object.values(state.bets)
                .filter(bet => isBetValid(bet, state))
                .filter(bet => bet.stake)
                .map(bet => {
                    const payload = generateBetPayload(bet, state, betSettings, isUsingCoins);
                    return referrer && hasImportedSelection(bet, state, referrer.optionIds)
                        ? { ...payload, share_referrer: { entry_id: referrer.entryId, link_id: referrer.linkId } }
                        : payload;
                }),
        ],
    };
};

const generateBetPayload = (bet: Bet, state: SbkBetSlipState, betSettings: BetSettings, isUsingCoins: boolean) => {
    switch (bet.betType) {
        case 'SINGLE':
            return generateSinglesBet(bet, state, betSettings, isUsingCoins);
        case 'COMBO':
            return generateComboBet(bet, state, betSettings, isUsingCoins);
    }
};

/**
 * Generates a Singles bet payload
 */
const generateSinglesBet = (
    bet: Bet,
    state: SbkBetSlipState,
    betSettings: BetSettings,
    isUsingCoins: boolean
): PlaceBet => {
    const selection = state.selections[bet.id];
    if (!selection) {
        throw new GenerateBetsPayloadError(SINGLE_ERROR_PREFIX + BetPayloadError.MissingSelection);
    }
    const option = state.options[selection.id];
    if (!option) {
        throw new GenerateBetsPayloadError(SINGLE_ERROR_PREFIX + BetPayloadError.MissingOption);
    }
    if (!bet.stake) {
        throw new GenerateBetsPayloadError(SINGLE_ERROR_PREFIX + BetPayloadError.MissingStake);
    }

    return {
        ...betSettings,
        is_fixed_odds: true,
        bet_type: 'SINGLE',
        stake_per_line: bet.stake,
        use_free_money: isFreeMoneyEnabled(isUsingCoins, state.isBetrBucksEnabled[bet.id]),
        currency: getCurrency(isUsingCoins, state.isBetrBucksEnabled[bet.id], betSettings.currency),
        promos: getBetPromos(state, bet.id, isUsingCoins),
        selections: [
            {
                selection_type: 'WIN',
                parts: [
                    {
                        fixture_id: selection.eventId,
                        side_bet_id: selection.marketId,
                        winner_id: selection.id,
                        type: 'WIN',
                        order: 1,
                        odds: option.odds,
                        starting_price: false,
                        best_odds_guaranteed: false,
                    },
                ],
            },
        ],
    };
};

const parlayBetTypes: { [key: number]: BetType } = {
    2: 'DOUBLE',
    3: 'TREBLE',
    4: 'FOURFOLD',
    5: 'FIVEFOLD',
    6: 'SIXFOLD',
    7: 'SEVENFOLD',
    8: 'EIGHTFOLD',
    9: 'NINEFOLD',
    10: 'TENFOLD',
    11: 'ELEVENFOLD',
    12: 'TWELVEFOLD',
    13: 'THIRTEENFOLD',
    14: 'FOURTEENFOLD',
    15: 'FIFTEENFOLD',
    16: 'SIXTEENFOLD',
    17: 'SEVENTEENFOLD',
    18: 'EIGHTEENFOLD',
    19: 'NINETEENFOLD',
    20: 'TWENTYFOLD',
} as const;

const generateSelectionPayload = ({
    selectionType,
    selection,
    selectionId,
    odds,
}: {
    selectionType: BetSelectionType;
    selection: Selection;
    selectionId: string;
    odds: number;
}) =>
    ({
        selection_type: selectionType,
        parts: [
            {
                fixture_id: selection.eventId,
                side_bet_id: selection.marketId,
                winner_id: selectionId,
                type: 'WIN',
                order: 1,
                odds,
                starting_price: false,
                best_odds_guaranteed: false,
            },
        ],
    } as PlaceBetSelection);

/**
 * Generates a Combo bet payload
 */
const generateComboBet = (
    bet: Bet,
    state: SbkBetSlipState,
    betSettings: BetSettings,
    isUsingCoins: boolean
): PlaceBet => {
    const comboBetType = getComboBetType(state);
    const { selections, options } = state;
    if (comboBetType === 'SGP') {
        return generateSgpBet(bet, state, betSettings, isUsingCoins);
    }

    if (comboBetType === 'SGP+') {
        return generateSgpPlusBet(bet, state, betSettings, isUsingCoins);
    }

    const selectionIds = Object.values(state.selections)
        .filter(selection => isComboSelectionEnabled(selection, state))
        .map(selection => selection.id); // filtered toggled on selections

    if (selectionIds.length < 2) {
        throw new GenerateBetsPayloadError(COMBO_ERROR_PREFIX + BetPayloadError.ComboMinSelections);
    }
    if (selectionIds.length > MAX_SELECTIONS) {
        throw new GenerateBetsPayloadError(COMBO_ERROR_PREFIX + BetPayloadError.ComboBetMaxSelections);
    }
    if (!bet.stake) {
        throw new GenerateBetsPayloadError(COMBO_ERROR_PREFIX + BetPayloadError.MissingStake);
    }
    const betSelections: PlaceBetSelection[] = [];
    selectionIds.map(selectionId => {
        const selection = selections[selectionId];
        if (!selection) {
            throw new GenerateBetsPayloadError(COMBO_ERROR_PREFIX + BetPayloadError.MissingSelection);
        }
        const option = options[selectionId];
        if (!option) {
            throw new GenerateBetsPayloadError(COMBO_ERROR_PREFIX + BetPayloadError.MissingOption);
        }
        betSelections.push(
            generateSelectionPayload({ selectionType: 'WIN', selection, selectionId, odds: option.odds })
        );
    });

    return {
        ...betSettings,
        is_fixed_odds: true,
        bet_type: parlayBetTypes[selectionIds.length],
        stake_per_line: bet.stake,
        use_free_money: isFreeMoneyEnabled(isUsingCoins, state.isBetrBucksEnabled[bet.id]),
        currency: getCurrency(isUsingCoins, state.isBetrBucksEnabled[bet.id], betSettings.currency),
        selections: betSelections,
        promos: getBetPromos(state, bet.id, isUsingCoins),
    };
};

/**
 * Generates a Sgp bet payload
 */
const generateSgpBet = (
    bet: Bet,
    state: SbkBetSlipState,
    betSettings: BetSettings,
    isUsingCoins: boolean
): PlaceBet => {
    const { selections, options, sgpOdds } = state;
    const selectionIds = Object.values(selections)
        .filter(selection => isComboSelectionEnabled(selection, state))
        .map(selection => selection.id);

    if (selectionIds.length < 2) {
        throw new GenerateBetsPayloadError(SGP_ERROR_PREFIX + BetPayloadError.ComboMinSelections);
    }
    if (selectionIds.length > MAX_SELECTIONS) {
        throw new GenerateBetsPayloadError(SGP_ERROR_PREFIX + BetPayloadError.ComboBetMaxSelections);
    }
    if (!bet.stake) {
        throw new GenerateBetsPayloadError(SGP_ERROR_PREFIX + BetPayloadError.MissingStake);
    }

    const sgpId = generateSgpOddsId(selectionIds, selections[selectionIds[0]].eventId);
    return {
        ...betSettings,
        is_fixed_odds: true,
        bet_type: parlayBetTypes[selectionIds.length],
        stake_per_line: bet.stake,
        use_free_money: isFreeMoneyEnabled(isUsingCoins, state.isBetrBucksEnabled[bet.id]),
        currency: getCurrency(isUsingCoins, state.isBetrBucksEnabled[bet.id], betSettings.currency),
        promos: getBetPromos(state, bet.id, isUsingCoins),
        selections: selectionIds.map(selectionId => {
            const selection = selections[selectionId];
            if (!selection) {
                throw new GenerateBetsPayloadError(SGP_ERROR_PREFIX + BetPayloadError.MissingSelection);
            }
            const option = options[selectionId];
            if (!option) {
                throw new GenerateBetsPayloadError(SGP_ERROR_PREFIX + BetPayloadError.MissingOption);
            }
            const odds = sgpOdds[sgpId];
            if (!odds) {
                throw new GenerateBetsPayloadError(SGP_ERROR_PREFIX + BetPayloadError.MissingSgpOdds);
            }

            return generateSelectionPayload({ selectionType: 'BETBUILDER', selection, selectionId, odds });
        }),
    };
};

/**
 * Generates a SgpPlus bet payload
 */
export const generateSgpPlusBet = (
    bet: Bet,
    state: SbkBetSlipState,
    betSettings: BetSettings,
    isUsingCoins: boolean
): PlaceBet => {
    const { selections, options, sgpOdds } = state;
    if (!bet.stake) {
        throw new GenerateBetsPayloadError(SGP_PLUS_ERROR_PREFIX + BetPayloadError.MissingStake);
    }

    // Group selections by event id to create selections payload for each event
    let groupedSelectionsByEventId: { [key: string]: Record<string, Selection> } = {};
    Object.values(selections)
        .filter(selection => isComboSelectionEnabled(selection, state))
        .map(selection => {
            groupedSelectionsByEventId[selection.eventId] = {
                ...groupedSelectionsByEventId[selection.eventId],
                [selection.id]: selection,
            };
        });

    const payloadSelections: PlaceBetSelection[] = [];
    Object.values(groupedSelectionsByEventId).forEach(eventSelections => {
        const selectionIds = Object.values(eventSelections).map(eventSelection => eventSelection.id);
        if (selectionIds.length < 2) {
            // If there is only one selection, it should be a single bet
            const selectionId = selectionIds[0];
            const selection = selections[selectionId];
            if (!selection) {
                throw new GenerateBetsPayloadError(SGP_PLUS_ERROR_PREFIX + BetPayloadError.MissingSelection);
            }
            const option = options[selectionId];
            if (!option) {
                throw new GenerateBetsPayloadError(SGP_PLUS_ERROR_PREFIX + BetPayloadError.MissingOption);
            }
            payloadSelections.push(
                generateSelectionPayload({ selectionType: 'WIN', selection, selectionId, odds: option.odds })
            );
        } else {
            // If there are more than one selection, it should be a sgp bet
            if (selectionIds.length > MAX_SELECTIONS) {
                throw new GenerateBetsPayloadError(SGP_PLUS_ERROR_PREFIX + BetPayloadError.ComboBetMaxSelections);
            }

            const sgpId = generateSgpOddsId(selectionIds, eventSelections[selectionIds[0]].eventId);
            selectionIds.forEach(selectionId => {
                const selection = selections[selectionId];
                if (!selection) {
                    throw new GenerateBetsPayloadError(SGP_PLUS_ERROR_PREFIX + BetPayloadError.MissingSelection);
                }
                const option = options[selectionId];
                if (!option) {
                    throw new GenerateBetsPayloadError(SGP_PLUS_ERROR_PREFIX + BetPayloadError.MissingOption);
                }
                const odds = sgpOdds[sgpId];
                if (!odds) {
                    throw new GenerateBetsPayloadError(SGP_PLUS_ERROR_PREFIX + BetPayloadError.MissingSgpOdds);
                }

                payloadSelections.push(
                    generateSelectionPayload({ selectionType: 'BETBUILDER', selection, selectionId, odds })
                );
            });
        }
    });
    return {
        ...betSettings,
        is_fixed_odds: true,
        bet_type: parlayBetTypes[payloadSelections.length],
        stake_per_line: bet.stake,
        use_free_money: isFreeMoneyEnabled(isUsingCoins, state.isBetrBucksEnabled[bet.id]),
        currency: getCurrency(isUsingCoins, state.isBetrBucksEnabled[bet.id], betSettings.currency),
        selections: payloadSelections,
        promos: getBetPromos(state, bet.id, isUsingCoins),
    };
};
